import { Api } from '../../Api/Api';
import { AppDispatch, RootState } from '../Store';
import { IDraftQuoteResponse } from '../../Models/Responses/DraftQuoteResponse';
import { createTracedAsyncThunk } from '../../../tracing/trace';

export interface IDraftQuote
{
	Quote: IDraftQuoteResponse;
	RequestedGrowerId: string;
}

export const getDraftQuote = createTracedAsyncThunk<IDraftQuote, {}, { dispatch: AppDispatch, state: RootState }>(
	'draftquote/get',
	async (context, request: {}, thunkAPI) => 
	{
		try
		{
			const { auth, ui } = thunkAPI.getState() as RootState;
			const api = new Api('/api/6', auth.userAuthToken, context);
			const selectedGrowerId = ui.SelectedGrowerId;
			const selectedPlanId = ui.SelectedPlanId;
			
			const response = await api.getAsync<IDraftQuoteResponse>(`plans/${selectedPlanId}/quote`);

			if (response.ErrorCode === null && response.Success) 
			{
				const quoteResponse: IDraftQuote = {
					Quote: response.Data,
					RequestedGrowerId: selectedGrowerId
				};
				return quoteResponse;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export interface ISendQuoteRequest
{
	CanSendOrders: boolean;
	SendQuote: boolean;
	QuoteId: string;
	ShipFrom: string;
	QuoteRequestLineUpdates: IQuoteRequestLineUpdate[];
	SeedAssignmentUpdateRequests?: ISeedAssignmentUpdateRequest[]; // Field Plan Quote only
}

export interface IQuoteRequestLineUpdate
{
	Id?: string; // Do not send if new line, else send the existing QuoteLine Id
	HybridId: string;
	TreatmentId: string;
	QuantityQB?: number;
	QuantityPB?: number;
	QuantityTB?: number;
	QuantityTO?: number;
	Rate: number;
	AppliedAcres: number;
}

export interface ISeedAssignmentUpdateRequest
{
	Id?: string; // Do not send if new assignment, else send the existing SeedAssignmentId
	FieldId: string;
	Year: number;
	HybridId: string;
	SeedingRate: number;
	AppliedAcres: number;
	TreatmentId: string;
}

export interface ISingleQuoteResponse
{
	CanSendOrders: boolean;
	Quote: IDraftQuoteResponse;
	RequestedGrowerId: string;
	SentToCropEdge: boolean;
}

export const sendQuote = createTracedAsyncThunk<ISingleQuoteResponse, ISendQuoteRequest, { dispatch: AppDispatch, state: RootState }>(
	'quote/send',
	async (context, request: ISendQuoteRequest, thunkAPI) => 
	{
		try
		{
			const api = new Api('/api/6', thunkAPI.getState().auth.userAuthToken, context);
			const selectedGrowerId = thunkAPI.getState().ui.SelectedGrowerId;
			const { SendQuote: sendQuote, QuoteId: quoteId, CanSendOrders } = request;
			
			const response = await api.postAsync<IDraftQuoteResponse>(`quotes/${quoteId}?send=${sendQuote}`, request);

			if (response.ErrorCode === null && response.Success) 
			{
				const quoteResponse: ISingleQuoteResponse =
				{
					CanSendOrders: CanSendOrders,
					Quote: response.Data,
					RequestedGrowerId: selectedGrowerId,
					SentToCropEdge: sendQuote
				};
				return quoteResponse;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);